import { type BulkEmailDto, type EmailBulkEmailUsersError, useEmailBulkEmailUsers } from "@crowdcoursing/api/openapi";

import { useToast } from "~hooks/useToast";

const tErrorSendingEmail = "Failed to send email";
const tSuccessSendingEmail = "Successfully sent email to {count} recipients";
const tValidationError = "Please fill in all fields and select at least one recipient";

interface UseBulkEmailOptions {
  onSuccess?: (recipientCount: number) => void;
  onError?: (error: EmailBulkEmailUsersError) => void;
}

interface BulkEmailData extends Omit<BulkEmailDto, "bubbleId"> {
  bubbleId: string;
}

export const useBulkEmail = ({ onSuccess, onError }: UseBulkEmailOptions = {}) => {
  const toast = useToast();

  const { mutate, status } = useEmailBulkEmailUsers({
    onError: (error: EmailBulkEmailUsersError) => {
      toast.error(tErrorSendingEmail);
      onError?.(error);
    },
    onSuccess: (_data, variables) => {
      const recipientCount = variables.body?.recipients?.length ?? 0;
      toast.success(tSuccessSendingEmail.replace("{count}", String(recipientCount)));
      onSuccess?.(recipientCount);
    },
  });

  const sendEmail = async (emailData: BulkEmailData) => {
    if (!emailData.subject || !emailData.emailContents || !emailData.recipients?.length) {
      toast.error(tValidationError);
      throw new Error(tValidationError);
    }

    return mutate({
      body: emailData,
    });
  };

  return {
    sendEmail,
    status,
  };
};
